/* eslint-disable no-unused-vars */
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Link } from 'react-router-dom';
import { Tag } from 'primereact/tag';
import { useMemo, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { useQuery } from 'react-query';
import { useGetAll, useGetAllStatus } from 'utils/hooks/projectProposals';
import Pagination from 'components/Pagination';
import { getProjectTypes } from 'utils/func';
import { getAllByUser } from 'apis/organizations.api';
import { getAllStatuses } from 'apis/projectProposals.api';
import { useGetAllByUser } from 'utils/hooks/organizations';

export default function ViewListDialog({ visible, setVisible, programId }) {
  const { t } = useTranslation();

  const [pagination, setPagination] = useState({
    first: 0,
    rowsPerPage: 10,
  });
  const [showMode, setShowMode] = useState('hideDeletedProjects');

  const [filtersTable, setFiltersTable] = useState({
    _vietnameseName: { value: null },
    _projectType: { value: null },
    _ownerName: { value: null },
    _organization: { value: null },
    _yearProposed: { value: null },
    _status: { value: null },
  });

  const [filters, setFilters] = useState({
    vietnameseName: null,
    projectType: null,
    ownerName: null,
    organizationId: null,
    yearProposed: null,
    statusName: null,
  });

  const [appliedFilters, setAppliedFilters] = useState({
    vietnameseName: null,
    projectType: null,
    ownerName: null,
    organizationId: null,
    yearProposed: null,
    statusName: null,
  });

  const projectTypes = getProjectTypes(t);

  const { data, isLoading } = useGetAll(
    pagination,
    { ...appliedFilters, programId: [programId] },
    showMode,
    {
      enabled: !!programId,
    }
  );

  const programName = useMemo(() => data?.data?.[0]?.program?.name ?? '', [data]);

  const { data: organizations } = useGetAllByUser(t, {});

  const yearProposedOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= 2019; i -= 1) {
      years.push({ value: i });
    }
    return years;
  }, []);

  const { data: statuses } = useGetAllStatus(t, {});

  const formatHeader = () => (
    <>
      {t('program.viewProjectProposal.dialogTitle')} {programName}
    </>
  );

  // Format name
  const formatName = (value) => {
    if (value?.deletedAt === null) {
      return (
        <Link to={`/projects-revise/${value?.reference}?tab=0`} className="hover:underline">
          {value?.vietnameseName}
        </Link>
      );
    }

    return <span>{value?.vietnameseName}</span>;
  };

  // Format projectType
  const formatProjectType = (value) => t(`projectTypes.${value.projectType}`);

  // Format owner
  const formatOwner = (value) => {
    const owner = value?.projectProposalContacts.find(
      (contact) => contact?.projectRoleId === 'e609455d-e481-492e-8fca-f631ebf0d464'
    );
    if (owner) return <div>{owner.fullname}</div>;
    return null;
  };

  // Format organization
  const formatOrganization = (value) =>
    t('lang') === 'vi'
      ? value?.projectProposalOrganizations[0]?.name
      : value?.projectProposalOrganizations[0]?.englishName;

  // Format status
  const formatStatus = (value) => {
    const statusColorMapping = {
      revise: 'warning',
      new: 'info',
      submit: 'success',
      approved: 'success',
    };

    return (
      <Tag
        style={{ maxWidth: '8rem' }}
        severity={statusColorMapping[value?.status?.name]}
        value={t(`statuses.${value?.status?.name}`)}
        rounded
      />
    );
  };

  // Filters
  const vietnameseNameFilter = () => (
    <InputText
      value={filters.vietnameseName}
      onChange={(e) => {
        const newFilters = { ...filters, vietnameseName: e.target.value };
        setFilters(newFilters);
      }}
      placeholder={t('projectProposal.viewList.name')}
      className="p-column-filter"
    />
  );

  const projectTypeFilter = () => (
    <MultiSelect
      value={filters.projectType}
      onChange={(e) => {
        const newFilters = { ...filters, projectType: e.value };
        setFilters(newFilters);
      }}
      options={projectTypes}
      optionLabel="name"
      optionValue="value"
      filter
      showClear
      placeholder={t('projectProposal.viewList.projectType')}
      maxSelectedLabels={3}
      style={{ maxWidth: '16rem', width: '12.5rem' }}
    />
  );

  const ownerNameFilter = () => (
    <InputText
      value={filters.ownerName}
      onChange={(e) => {
        const newFilters = { ...filters, ownerName: e.target.value };
        setFilters(newFilters);
      }}
      placeholder={t('projectProposal.viewList.owner')}
      className="p-column-filter"
    />
  );

  const organizationFilter = () => (
    <MultiSelect
      value={filters.organizationId}
      onChange={(e) => {
        const newFilters = { ...filters, organizationId: e.value };
        setFilters(newFilters);
      }}
      options={organizations}
      optionLabel="name"
      optionValue="id"
      filter
      showClear
      placeholder={t('projectProposal.viewList.organization')}
      maxSelectedLabels={3}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
    />
  );
  const yearProposedFilter = () => (
    <MultiSelect
      value={filters.yearProposed}
      onChange={(e) => {
        const newFilters = { ...filters, yearProposed: e.value };
        setFilters(newFilters);
      }}
      options={yearProposedOptions}
      optionLabel="value"
      optionValue="value"
      filter
      showClear
      placeholder={t('projectProposal.general.researchTime.yearProposed')}
      maxSelectedLabels={3}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
    />
  );

  const statusFilter = () => (
    <MultiSelect
      value={filters.statusName}
      options={statuses}
      onChange={(e) => {
        const newFilters = { ...filters, statusName: e.value };
        setFilters(newFilters);
      }}
      optionLabel="name"
      optionValue="code"
      showClear
      filter
      placeholder={t('projectProposal.viewList.status')}
      style={{ minWidth: '12rem' }}
      maxSelectedLabels={3}
    />
  );

  const filterClearTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.clear')}
      onClick={() => {
        options.filterClearCallback();
      }}
      severity="secondary"
      outlined
    />
  );

  const filterApplyTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.apply')}
      onClick={() => {
        options.filterApplyCallback();
      }}
      severity="success"
    />
  );

  const filterActiveStyle = (field) =>
    filters[field] && (filters[field]?.length > 0 || Object.values(filters[field]).some((e) => e))
      ? { color: '#22C55E' }
      : { color: '#6c757d' };

  return (
    <Dialog
      header={formatHeader}
      visible={visible}
      style={{ width: '80vw', maxHeight: '40vw', color: 'blue' }}
      onHide={() => setVisible(false)}
      headerStyle={{ color: '#3B82F6' }}
    >
      <DataTable
        value={data?.data}
        className="p-datatable-gridlines header-table"
        dataKey="id"
        emptyMessage={t('projectProposal.viewList.emptyMessage')}
        filters={filtersTable}
        filterDisplay="menu"
        loading={isLoading}
      >
        <Column
          body={formatName}
          header={t('projectProposal.viewList.name')}
          headerStyle={filterActiveStyle('vietnameseName')}
          style={{ textAlign: 'left', minWidth: '20rem' }}
          filter
          filterField="_vietnameseName"
          filterElement={vietnameseNameFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, vietnameseName: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          frozen
          alignFrozen="left"
        />
        <Column
          body={formatProjectType}
          header={t('projectProposal.viewList.projectType')}
          headerStyle={filterActiveStyle('projectType')}
          filter
          filterField="_projectType"
          filterElement={projectTypeFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, projectType: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ textAlign: 'center' }}
        />
        <Column
          body={formatOwner}
          header={t('projectProposal.viewList.owner')}
          headerStyle={filterActiveStyle('ownerName')}
          filter
          filterField="_ownerName"
          filterElement={ownerNameFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, ownerName: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ textAlign: 'left' }}
        />
        <Column
          body={formatOrganization}
          header={t('projectProposal.viewList.organization')}
          headerStyle={filterActiveStyle('organizationId')}
          filter
          filterField="_organization"
          filterElement={organizationFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu={organizations?.length > 1}
          onFilterClear={() => {
            const newFilters = { ...filters, organizationId: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ textAlign: 'center' }}
        />
        <Column
          field="yearProposed"
          header={t('projectProposal.general.researchTime.yearProposed')}
          headerStyle={filterActiveStyle('yearProposed')}
          filter
          filterField="_yearProposed"
          filterElement={yearProposedFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, yearProposed: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ textAlign: 'center' }}
        />
        <Column
          body={formatStatus}
          header={t('projectProposal.viewList.status')}
          headerStyle={filterActiveStyle('statusName')}
          filter
          filterField="_status"
          filterElement={statusFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMatchModes={false}
          showFilterMenu
          onFilterClear={() => {
            const newFilters = { ...filters, statusName: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          style={{ textAlign: 'center' }}
        />
      </DataTable>
      <Pagination
        pagination={pagination}
        setPagination={setPagination}
        paginationData={data.pagination}
        showMode={showMode}
        setShowMode={setShowMode}
        pageName="projects-revise"
      />
    </Dialog>
  );
}

ViewListDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  programId: PropTypes.string.isRequired,
};
